/**
 * Main module entities for Projects/Tasks domain
 * These entities are used across presentation layer, domain hooks, and API.
 */
export enum ProjectPriorityLabel {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}
/**
 * Represents a Project that the user tracks (one website per project).
 * Summary stats are denormalized for dashboard performance.
 */
export interface Project {
  id: string;
  name: string;
  baseUrl: string;
  // Task stats
  openTasksCount: number;
  totalTasksCount: number;
  /**
   * Percent complete (0..100). The server should compute and persist this
   * based on open vs completed tasks. The UI will animate the progress bar.
   */
  percentComplete: number;
  /**
   * The timestamp of the last analysis run for this project.
   * Stored as UTC on the server.
   */
  lastAnalysisAt: Date | null;
  /**
   * A simple health priority label used in the card badge:
   * "high" means project needs attention.
   */
  priorityLabel: ProjectPriorityLabel;
  createdAt: Date;
  updatedAt: Date | null;
}
/**
 * Categories of optimization tasks supported by the system.
 */
export enum TaskCategory {
  SEO = "seo",
  AEO = "aeo",
  TECHNICAL = "technical",
  CONTENT = "content",
  ANALYTICS = "analytics",
  OTHER = "other",
}
/**
 * Impact level the task is expected to have.
 */
export enum TaskImpact {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}
/**
 * Implementation effort for a task.
 */
export enum TaskEffort {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
}
/**
 * Status of a task.
 */
export enum TaskStatus {
  OPEN = "open",
  COMPLETED = "completed",
}
/**
 * Source of a task, used to distinguish manual vs AI-generated.
 * Example values: "Manual", "Analysis run #3 on 2025-01-10"
 */
export interface TaskSource {
  label: string;
}
/**
 * Represents a task item attached to a project.
 */
export interface Task {
  id: string;
  projectId: string;
  title: string;
  description: string | null;
  category: TaskCategory;
  impact: TaskImpact;
  effort: TaskEffort | null;
  relatedUrl: string | null;
  source: TaskSource;
  status: TaskStatus;
  /**
   * System-generated or user-editable list of specific, actionable steps to remedy the identified problem.
   * Each string represents a distinct, granular action item.
   */
  recommendedActions: string[] | null;
  createdAt: Date;
  updatedAt: Date | null;
  completedAt: Date | null;
}
/**
 * KPI stats displayed in Project Overview panel.
 */
export interface ProjectKpis {
  openTasks: number;
  totalTasks: number;
  percentComplete: number;
  lastAnalysisDate: Date | null;
  lastRunNumber: number | null;
  highImpactOpen: number;
}
/**
 * Category breakdown aggregations for Project Overview.
 */
export interface CategoryBreakdownItem {
  category: TaskCategory;
  openCount: number;
  totalCount: number;
  percentComplete: number;
}
export interface CategoryBreakdown {
  items: CategoryBreakdownItem[];
}
/**
 * Recent activity timeline item for last 7 days section.
 */
export enum ActivityType {
  TASK_COMPLETED = "task_completed",
  ANALYSIS_RUN = "analysis_run",
  TASK_EDITED = "task_edited",
}
export interface ActivityItem {
  id: string;
  type: ActivityType;
  description: string;
  occurredAt: Date;
}